import { ChangeEvent, useId, useState } from "react";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ReactComponent as LinkIcon } from "../../../assets/link.icon.svg";
import { ReactComponent as CloudIcon } from "../../../assets/cloud.icon.svg";
import { ReactComponent as CloudErrorIcon } from "../../../assets/cloud--error.icon.svg";
import { addContentItemProvider } from "../../../api/admin/content-providers/endpoints-referrers";
import { Loader } from "../../../components/loader/loader.comp";
import { LoaderStep } from "../../../common/enums/loader-step.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import {
    DropdownMenuType,
    SelectionFilter
} from "../../../components/menu-filter/menu-filter.comp";
import CrawlSettingsForm from "../../../components/crawl-settings/crawl-settings-form.comp";
import {
    CrawlSettingsValues,
    emptyCrawlSettings,
    toCrawlConfig,
    validateCrawlSettings
} from "../../../components/crawl-settings/crawl-settings.util";

/**
 * "Add Source → Web site" (web_scraper_provider_design.md §10, BR-34, BR-10,
 * D6, D16). Creates a webScraper provider; the first crawl stops for review
 * before anything is indexed, so the completion step says so instead of
 * "files will appear soon".
 */
const LinkWebSiteView = ({
    handleClose,
    handleSuccess,
    initialName = "",
    sourceTypes
}: {
    handleClose: () => void;
    handleSuccess: () => void;
    initialName?: string;
    /** Rendered when this form is one choice of the shared Add Source chooser. */
    sourceTypes?: { list: string[]; selected: string; onSelect: (type: string) => void };
}) => {
    const { user } = FirebaseAuthContainer.useContainer();
    const fieldId = useId();
    const [name, setName] = useState<string>(initialName);
    const [values, setValues] = useState<CrawlSettingsValues>(emptyCrawlSettings);
    const [acknowledged, setAcknowledged] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const errors = (() => {
        const list = name.trim() ? [] : ["Enter a name for this source."];
        list.push(...validateCrawlSettings(values));
        if (!acknowledged) {
            list.push(
                "Confirm that your organization has the right to use this site's content, including the documents it links to."
            );
        }
        return list;
    })();

    const handleNameChange = (evt: ChangeEvent<HTMLInputElement>) =>
        setName(evt.target.value ?? "");

    const handleAddClick = () => {
        if (!user?.companyId || !user?.currentKnowledgeBaseId) return;
        if (errors.length) {
            setShowErrors(true);
            return;
        }
        setStep(LoaderStep.loading);
        addContentItemProvider(user.companyId, {
            name: ProviderType.webScraper,
            displayName: name.trim(),
            knowledgeBaseId: user.currentKnowledgeBaseId,
            crawlConfig: toCrawlConfig(values, "create"),
            acknowledged: true,
            robotsOverride: values.robotsOverride && values.robotsAttested,
            reviewPolicy: values.reviewPolicy,
            crawlSchedule: values.crawlSchedule
        })
            .then(() => {
                setStep(LoaderStep.complete);
                handleSuccess();
            })
            .catch(error => {
                const serverMessage = error?.response?.data?.message;
                switch (error?.code) {
                    case "ERR_NETWORK":
                        setErrorMessage("Network error.");
                        break;
                    case "ERR_BAD_REQUEST":
                        setErrorMessage(
                            typeof serverMessage === "string"
                                ? serverMessage
                                : `There was a problem adding the web site "${name}". Check the start URLs and limits.`
                        );
                        break;
                    default:
                        setErrorMessage("Something went wrong. Please try again later.");
                }
                setStep(LoaderStep.error);
            });
    };

    return (
        <div className="link-web-site">
            {step === LoaderStep.start && (
                <>
                    <div className="link-web-site-title">
                        <SvgIcon
                            className="link-web-site-title__icon"
                            component={LinkIcon}
                            inheritViewBox
                        />
                        <span className="link-web-site-title__text" role="heading" aria-level={1}>
                            Add Source
                        </span>
                    </div>
                    <div className="link-web-site-description">
                        <span className="link-web-site-description__text">
                            Crawl a public web site. The first crawl stops for your review before
                            any page is indexed, and you choose what to keep.
                        </span>
                    </div>
                    <div className="link-web-site-body">
                        <input
                            type="text"
                            placeholder="Source Name"
                            aria-label="Source Name"
                            onChange={handleNameChange}
                            value={name}
                        />
                        {sourceTypes && (
                            <SelectionFilter
                                className="link-web-site-selection"
                                list={sourceTypes.list}
                                selectedItem={sourceTypes.selected}
                                setSelectedItem={sourceTypes.onSelect}
                                placeholder="Select Source Type"
                                dropdownMenuType={DropdownMenuType.wider}
                                rightPosition={true}
                                minWidth={534}
                            />
                        )}
                        <CrawlSettingsForm values={values} onChange={setValues} mode="create" />
                        <div className="link-web-site-acknowledge">
                            <input
                                type="checkbox"
                                id={`${fieldId}-acknowledge`}
                                checked={acknowledged}
                                onChange={evt => setAcknowledged(evt.target.checked)}
                            />
                            <label htmlFor={`${fieldId}-acknowledge`}>
                                My organization has the right to use this site&apos;s content,
                                including the documents its pages link to, in this Assistant.
                            </label>
                        </div>
                        {showErrors && errors.length > 0 && (
                            <div className="link-web-site-errors" role="alert">
                                <span className="link-web-site-errors__title">
                                    Please fix the following:
                                </span>
                                <ul>
                                    {errors.map(error => (
                                        <li key={error}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </>
            )}
            {step === LoaderStep.loading && (
                <div className="link-source-loading">
                    <div className="link-source-loading-title">
                        <Loader size={40} borderWidth={5} />
                        <span className="link-source-loading-title__text">Adding Web Site</span>
                    </div>
                    <span className="link-source-loading-description">
                        {`You can now close this window.
                        The crawl runs in the background and stops for your review.`}
                    </span>
                </div>
            )}
            {step === LoaderStep.complete && (
                <div className="link-source-complete">
                    <div className="link-source-complete-title">
                        <SvgIcon
                            className="link-source-complete-title__icon"
                            component={CloudIcon}
                            inheritViewBox
                        />
                        <span className="link-source-complete-title__text">Web Site Added</span>
                    </div>
                    <span className="link-source-complete-description">
                        {`The site is being crawled. When the crawl finishes, a banner on the source's
                        file list asks you to review what was found before it is indexed.`}
                    </span>
                </div>
            )}
            {step === LoaderStep.error && (
                <div className="link-source-error">
                    <div className="link-source-error-title">
                        <SvgIcon
                            className="link-source-error-title__icon"
                            component={CloudErrorIcon}
                            inheritViewBox
                        />
                        <span className="link-source-error-title__text">
                            Could Not Add Web Site
                        </span>
                    </div>
                    <span className="link-source-error-description" role="alert">
                        {errorMessage}
                    </span>
                </div>
            )}
            <div className="link-web-site-action">
                {(step === LoaderStep.start || step === LoaderStep.loading) && (
                    <>
                        <Button
                            className="thirt-button"
                            variant="text"
                            onClick={handleClose}
                            disabled={step === LoaderStep.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="primary-button second-button"
                            variant="text"
                            onClick={handleAddClick}
                            disabled={step === LoaderStep.loading}
                        >
                            {step === LoaderStep.start ? "Add Source" : "Please wait"}
                        </Button>
                    </>
                )}
                {(step === LoaderStep.complete || step === LoaderStep.error) && (
                    <Button variant="text" className="primary-button" onClick={handleClose}>
                        Close
                    </Button>
                )}
            </div>
        </div>
    );
};

export default LinkWebSiteView;
